import type { HTTPClient, APIOptions } from '../core/http-client.js';
import { API_ENDPOINTS } from '../core/constants.js';
import type { SendMessageRequest, SenderAction, Recipient, ReactionPayload } from '../types/messages.js';
import type { SendMessageResponse } from '../types/responses.js';
import type { AttachmentType } from '../types/attachments.js';
import { validateTextMessage } from '../utils/message-validators.js';

export class SendAPI {
  constructor(private httpClient: HTTPClient) { }

  async message(request: SendMessageRequest, options?: APIOptions): Promise<SendMessageResponse> {
    // Validate text message length if present
    if (request.message?.text) {
      validateTextMessage(request.message.text);
    }

    return this.httpClient.request<SendMessageResponse>({
      method: 'POST',
      path: API_ENDPOINTS.MESSAGES,
      body: request,
      accessToken: options?.accessToken,
    });
  }

  async action(recipientId: string, action: SenderAction, options?: APIOptions & { payload?: ReactionPayload }): Promise<SendMessageResponse> {
    const body: any = {
      recipient: { id: recipientId },
      messaging_type: 'RESPONSE',
      sender_action: action,
    };

    if (options?.payload) {
      body.payload = options.payload;
    }

    return this.httpClient.request<SendMessageResponse>({
      method: 'POST',
      path: API_ENDPOINTS.MESSAGES,
      body,
      accessToken: options?.accessToken,
    });
  }

  async typingOn(recipientId: string, options?: APIOptions): Promise<SendMessageResponse> {
    return this.action(recipientId, 'typing_on', options);
  }

  async typingOff(recipientId: string, options?: APIOptions): Promise<SendMessageResponse> {
    return this.action(recipientId, 'typing_off', options);
  }

  async markSeen(recipientId: string, options?: APIOptions): Promise<SendMessageResponse> {
    return this.action(recipientId, 'mark_seen', options);
  }

  async setTyping(recipientId: string, state: boolean, options?: APIOptions): Promise<SendMessageResponse> {
    return state ? this.typingOn(recipientId, options) : this.typingOff(recipientId, options);
  }

  async markRead(recipientId: string, options?: APIOptions): Promise<SendMessageResponse> {
    return this.markSeen(recipientId, options);
  }

  async addReaction(recipientId: string, content: { messageId: string, emoji: string }, options?: APIOptions): Promise<SendMessageResponse> {
    return this.action(recipientId, 'react', {
      ...options,
      payload: {
        message_id: content.messageId,
        reaction: content.emoji
      }
    });
  }

  async removeReaction(recipientId: string, messageId: string, options?: APIOptions): Promise<SendMessageResponse> {
    return this.action(recipientId, 'unreact', {
      ...options,
      payload: {
        message_id: messageId
      }
    });
  }

  // Convenience methods for sending attachments

  /**
   * Send an attachment using a previously uploaded attachment_id
   */
  async attachment(options: {
    recipient: Recipient;
    type: AttachmentType;
    attachment_id: string;
    messaging_type?: 'RESPONSE' | 'UPDATE' | 'MESSAGE_TAG';
  }, apiOptions?: APIOptions): Promise<SendMessageResponse> {
    return this.message({
      recipient: options.recipient,
      messaging_type: options.messaging_type ?? 'RESPONSE',
      message: {
        attachment: {
          type: options.type,
          payload: {
            attachment_id: options.attachment_id,
          },
        },
      },
    }, apiOptions);
  }

  /**
   * Upload and send an attachment from URL in a single request
   */
  async attachmentFromUrl(options: {
    recipient: Recipient;
    type: AttachmentType;
    url: string;
    messaging_type?: 'RESPONSE' | 'UPDATE' | 'MESSAGE_TAG';
  }, apiOptions?: APIOptions): Promise<SendMessageResponse> {
    return this.message({
      recipient: options.recipient,
      messaging_type: options.messaging_type ?? 'RESPONSE',
      message: {
        attachment: {
          type: options.type,
          payload: {
            url: options.url,
          },
        },
      },
    }, apiOptions);
  }
}